import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { 
  Users, Building2, Search, Trash2, CheckCircle, XCircle, Loader2, 
  ShieldX, ArrowLeft, Download, Upload, Settings, Eye, EyeOff, UserPlus, BarChart3
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// 4 Strategy groups (each holds 3 sub-topics). Admin now toggles at group level.
const STRATEGY_GROUPS = [
  { key: "strategy_market_group",     label: "Market, Value Chain & Competitors", desc: "Market · Value Chain · Competitor Landscape",         legacy: ["strategy_markets", "strategy_value_chain", "strategy_competitors"] },
  { key: "strategy_clinical_group",   label: "Clinical & Regulatory",              desc: "Clinical Proof · Regulatory Route · Access & HEOR",   legacy: ["strategy_clinical_proof", "strategy_regulatory_route", "strategy_access_heor"] },
  { key: "strategy_technology_group", label: "Technology, IP & Business Model",    desc: "Technology · Defensibility · Business Model",         legacy: ["strategy_product", "strategy_defensibility", "strategy_value_capture"] },
  { key: "strategy_milestones_group", label: "Milestones, Team & Fundraising",     desc: "Milestones · Team · Funding Strategy",                legacy: ["strategy_value_inflection", "strategy_execution_gaps", "strategy_funding_fit"] },
];
const STRATEGY_GROUP_KEYS = STRATEGY_GROUPS.map((g) => g.key);
const NON_STRATEGY_ITEMS = [
  { key: "investors", label: "Investors",  desc: "Investor CRM & pipeline" },
  { key: "pharma",    label: "Pharma",     desc: "Pharma BD contacts" },
  { key: "grants",    label: "Grants",     desc: "Grant opportunities" },
  { key: "events",    label: "Events",     desc: "Networking events" },
];
const DEFAULT_VISIBILITY = (() => {
  const d = {};
  STRATEGY_GROUP_KEYS.forEach((k) => { d[k] = true; });
  NON_STRATEGY_ITEMS.forEach((n) => { d[n.key] = true; });
  d.hide_locked_modules = false;
  return d;
})();
const mergeVisibility = (data) => {
  const out = { ...DEFAULT_VISIBILITY };
  // Direct keys (group + non-strategy)
  Object.keys(out).forEach((k) => { if (data && data[k] !== undefined) out[k] = !!data[k]; });
  // Legacy migration — if a saved record only has the old 12 sub-keys, roll them up:
  // group = false if ANY of its 3 legacy sub-keys is explicitly false.
  if (data) {
    STRATEGY_GROUPS.forEach((g) => {
      if (data[g.key] === undefined) {
        const anyLegacyOff = g.legacy.some((s) => data[s] === false);
        if (anyLegacyOff) out[g.key] = false;
      }
      // Older whole-group legacy key (e.g. "strategy_market") without _group suffix
      const oldName = g.key.replace("_group", "");
      if (data[oldName] === false) out[g.key] = false;
    });
  }
  return out;
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(null);
  const [search, setSearch] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("all");
  const [total, setTotal] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleting, setDeleting] = useState(false);
  
  // Profile view dialog
  const [profileDialog, setProfileDialog] = useState({ open: false, user: null });
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  
  // Visibility settings
  const [visibilityDialog, setVisibilityDialog] = useState({ open: false, user: null });
  const [visibilitySettings, setVisibilitySettings] = useState({ ...DEFAULT_VISIBILITY });
  const [savingVisibility, setSavingVisibility] = useState(false);

  // Module completion overview
  const [leagueDialog, setLeagueDialog] = useState(false);
  const [leagueRows, setLeagueRows] = useState([]);
  const [leagueLoading, setLeagueLoading] = useState(false);
  const [completionDialog, setCompletionDialog] = useState({ open: false, user: null, data: null });
  const [completionLoading, setCompletionLoading] = useState(false);

  const fetchLeague = async () => {
    setLeagueLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/module-completion`, { credentials: "include" });
      if (res.ok) { const j = await res.json(); setLeagueRows(j.companies || []); }
    } catch (e) { console.error(e); }
    setLeagueLoading(false);
  };
  const openUserCompletion = async (u) => {
    setCompletionDialog({ open: true, user: u, data: null });
    setCompletionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${u.user_id}/module-completion`, { credentials: "include" });
      if (res.ok) setCompletionDialog({ open: true, user: u, data: await res.json() });
    } catch (e) { console.error(e); }
    setCompletionLoading(false);
  };
  const downloadCompletionCsv = () => {
    // Uses credentials from the existing cookie session
    const a = document.createElement("a");
    a.href = `${BACKEND_URL}/api/admin/module-completion.csv`;
    a.setAttribute("download", "module_completion.csv");
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  
  // Global visibility settings
  const [globalVisibility, setGlobalVisibility] = useState({ ...DEFAULT_VISIBILITY });
  const [globalVisibilityDialog, setGlobalVisibilityDialog] = useState(false);
  
  // Create user dialog
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    company: "",
    password: "",
    visibility: { ...DEFAULT_VISIBILITY }
  });

  useEffect(() => {
    checkAccess();
  }, []);

  useEffect(() => {
    if (isAdmin === true) {
      const debounce = setTimeout(fetchUsers, 300);
      return () => clearTimeout(debounce);
    }
  }, [search, verifiedFilter, isAdmin]);
  
  useEffect(() => {
    if (isAdmin === true) {
      fetchGlobalVisibility();
    }
  }, [isAdmin]);

  const checkAccess = async () => {
    try {
      const userRes = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
      if (!userRes.ok) { navigate("/auth"); return; }
      setUser(await userRes.json());

      const adminRes = await fetch(`${BACKEND_URL}/api/admin/check`, { credentials: "include" });
      if (adminRes.ok) {
        const data = await adminRes.json();
        setIsAdmin(data.is_admin);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      navigate("/auth");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (verifiedFilter !== "all") params.append("verified", verifiedFilter);

      const response = await fetch(`${BACKEND_URL}/api/admin/users?${params.toString()}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotal(data.total);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    setDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${deleteDialog.user.user_id}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("User deleted successfully");
        fetchUsers();
        setDeleteDialog({ open: false, user: null });
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to delete user");
      }
    } catch (error) {
      toast.error("Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users?limit=1000`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const headers = ["name", "email", "company", "auth_type", "email_verified", "created_at"];
        const csvRows = [headers.join(",")];
        
        for (const u of data.users) {
          const row = headers.map(h => {
            let val = u[h] || "";
            if (typeof val === "boolean") val = val ? "Yes" : "No";
            if (typeof val === "string" && (val.includes(",") || val.includes('"'))) {
              val = `"${val.replace(/"/g, '""')}"`;
            }
            return val;
          });
          csvRows.push(row.join(","));
        }
        
        const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `users_export_${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Users exported successfully");
      }
    } catch (error) {
      toast.error("Failed to export users");
    }
  };
  
  const handleViewProfile = async (targetUser) => {
    setProfileDialog({ open: true, user: targetUser });
    setLoadingProfile(true);
    setProfileData(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${targetUser.user_id}/profile`, { 
        credentials: "include" 
      });
      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
      } else {
        toast.error("Failed to fetch user profile");
      }
    } catch (error) {
      toast.error("Failed to fetch user profile");
    } finally {
      setLoadingProfile(false);
    }
  };
  
  // Visibility settings handlers
  const fetchGlobalVisibility = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/global-visibility`, { credentials: "include" });
      if (res.ok) {
        setGlobalVisibility(mergeVisibility(await res.json()));
      }
    } catch (e) {
      console.error("Failed to fetch global visibility:", e);
    }
  };
  
  const openVisibilityDialog = async (u) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${u.user_id}/visibility`, { credentials: "include" });
      if (res.ok) {
        setVisibilitySettings(mergeVisibility(await res.json()));
      } else {
        // Use global defaults if no user-specific settings
        setVisibilitySettings({ ...globalVisibility });
      }
    } catch (e) {
      setVisibilitySettings({ ...globalVisibility });
    }
    setVisibilityDialog({ open: true, user: u });
  };
  
  const saveVisibility = async () => {
    if (!visibilityDialog.user) return;
    setSavingVisibility(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${visibilityDialog.user.user_id}/visibility`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(visibilitySettings)
      });
      if (res.ok) {
        toast.success("Visibility settings saved");
        setVisibilityDialog({ open: false, user: null });
      } else {
        toast.error("Failed to save visibility settings");
      }
    } catch (e) {
      toast.error("Failed to save visibility settings");
    }
    setSavingVisibility(false);
  };
  
  const saveGlobalVisibility = async () => {
    setSavingVisibility(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/global-visibility`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(globalVisibility)
      });
      if (res.ok) {
        toast.success("Global visibility settings saved");
        setGlobalVisibilityDialog(false);
      } else {
        toast.error("Failed to save global visibility");
      }
    } catch (e) {
      toast.error("Failed to save global visibility");
    }
    setSavingVisibility(false);
  };

  // Create new user
  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Name, email, and password are required");
      return;
    }
    if (newUser.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setCreatingUser(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newUser)
      });
      
      if (res.ok) {
        toast.success(`User ${newUser.name} created successfully`);
        setCreateUserDialog(false);
        setNewUser({
          name: "",
          email: "",
          company: "",
          password: "",
          visibility: { investors: true, pharma: true, grants: true, events: true, strategy: true }
        });
        fetchUsers();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to create user");
      }
    } catch (e) {
      toast.error("Failed to create user");
    }
    setCreatingUser(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric"
    });
  };

  if (isAdmin === null || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="max-w-md mx-auto border border-red-200">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <ShieldX className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
              <p className="text-slate-500 mb-6">You don't have permission to access this page.</p>
              <Button onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Manage Users</h1>
            <p className="text-slate-600 mt-1">View and manage platform users</p>
          </div>
        </div>

        {/* Actions Bar */}
        <Card className="border border-slate-200 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex gap-3 flex-1">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-users"
                  />
                </div>
                <Select value={verifiedFilter} onValueChange={setVerifiedFilter}>
                  <SelectTrigger className="w-32" data-testid="verified-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Verified</SelectItem>
                    <SelectItem value="false">Unverified</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => setCreateUserDialog(true)} className="bg-teal-600 hover:bg-teal-700">
                <UserPlus className="w-4 h-4 mr-2" />
                Create User
              </Button>
              <Button variant="outline" onClick={() => setGlobalVisibilityDialog(true)}>
                <Settings className="w-4 h-4 mr-2" />
                Global Visibility
              </Button>
              <Button variant="outline" onClick={() => { setLeagueDialog(true); fetchLeague(); }} data-testid="open-completion-league-btn">
                <BarChart3 className="w-4 h-4 mr-2" />
                Module Completion
              </Button>
              <Button variant="outline" onClick={handleExport}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Users ({total})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {users.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No users found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>User</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Auth Type</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Profile</TableHead>
                      <TableHead>Visibility</TableHead>
                      <TableHead>Completion</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.user_id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                              {u.picture ? (
                                <img src={u.picture} alt={u.name} className="w-full h-full object-cover" />
                              ) : (
                                <span className="font-medium text-slate-600">{u.name?.charAt(0) || "?"}</span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{u.name}</p>
                              <p className="text-sm text-slate-500">{u.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {u.company ? (
                            <div className="flex items-center gap-1 text-slate-600">
                              <Building2 className="w-4 h-4" />
                              {u.company}
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {u.email_verified ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                              <XCircle className="w-3 h-3 mr-1" />
                              Unverified
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {u.auth_type || "google"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {formatDate(u.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-600 hover:text-teal-600"
                            onClick={() => handleViewProfile(u)}
                            data-testid={`view-profile-${u.user_id}`}
                          >
                            <Building2 className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-600 hover:text-teal-600"
                            onClick={() => openVisibilityDialog(u)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-slate-600 hover:text-teal-600"
                            onClick={() => openUserCompletion(u)}
                            data-testid={`view-completion-${u.user_id}`}
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-slate-400 hover:text-red-500"
                            onClick={() => setDeleteDialog({ open: true, user: u })}
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: deleteDialog.user })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.user?.name}</strong> ({deleteDialog.user?.email})?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* User Visibility Dialog */}
      <Dialog open={visibilityDialog.open} onOpenChange={(open) => setVisibilityDialog({ open, user: visibilityDialog.user })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Section Visibility</DialogTitle>
            <DialogDescription>
              Control which sections <strong>{visibilityDialog.user?.name}</strong> can access.
              Disabled sections will show as "locked" with a message.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="vis-row-hide-locked">
              <div>
                <p className="font-medium text-slate-900 text-sm">Hide locked modules entirely</p>
                <p className="text-xs text-slate-500 mt-0.5">When on, disabled modules are removed from the sidebar (no lock icon shown).</p>
              </div>
              <Switch
                data-testid="vis-toggle-hide-locked"
                checked={!!visibilitySettings.hide_locked_modules}
                onCheckedChange={(checked) => setVisibilitySettings({ ...visibilitySettings, hide_locked_modules: checked })}
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800 border-b pb-2 mb-2">Strategy Modules</p>
              <p className="text-xs text-slate-500 mb-3">Toggle a whole 3-topic module on or off. Sub-topics (e.g. Market, Value Chain, Competitor Landscape) are grouped and controlled together.</p>
              <div className="space-y-2">
                {STRATEGY_GROUPS.map((group) => (
                  <div key={group.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`vis-row-${group.key}`}>
                    <div>
                      <p className="font-medium text-slate-900">{group.label}</p>
                      <p className="text-xs text-slate-500">{group.desc}</p>
                    </div>
                    <Switch
                      data-testid={`vis-toggle-${group.key}`}
                      checked={!!visibilitySettings[group.key]}
                      onCheckedChange={(checked) => setVisibilitySettings({ ...visibilitySettings, [group.key]: checked })}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800 border-b pb-2 mb-2">Execution Modules</p>
              <div className="space-y-2">
                {NON_STRATEGY_ITEMS.map((section) => (
                  <div key={section.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`vis-row-${section.key}`}>
                    <div>
                      <p className="font-medium text-slate-900">{section.label}</p>
                      <p className="text-xs text-slate-500">{section.desc}</p>
                    </div>
                    <Switch
                      data-testid={`vis-toggle-${section.key}`}
                      checked={!!visibilitySettings[section.key]}
                      onCheckedChange={(checked) => setVisibilitySettings({ ...visibilitySettings, [section.key]: checked })}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVisibilityDialog({ open: false, user: null })}>
              Cancel
            </Button>
            <Button onClick={saveVisibility} disabled={savingVisibility}>
              {savingVisibility && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Global Visibility Dialog */}
      <Dialog open={globalVisibilityDialog} onOpenChange={setGlobalVisibilityDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Global Visibility Defaults</DialogTitle>
            <DialogDescription>
              Set default section visibility for all new users. Individual user settings override these defaults.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="gvis-row-hide-locked">
              <div>
                <p className="font-medium text-slate-900 text-sm">Hide locked modules entirely (default)</p>
                <p className="text-xs text-slate-500 mt-0.5">Default for new users. When on, disabled modules are removed from the sidebar (no lock icon).</p>
              </div>
              <Switch
                data-testid="gvis-toggle-hide-locked"
                checked={!!globalVisibility.hide_locked_modules}
                onCheckedChange={(checked) => setGlobalVisibility({ ...globalVisibility, hide_locked_modules: checked })}
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800 border-b pb-2 mb-2">Strategy Modules</p>
              <p className="text-xs text-slate-500 mb-3">Default visibility for new users — each toggle controls a whole 3-topic module.</p>
              <div className="space-y-2">
                {STRATEGY_GROUPS.map((group) => (
                  <div key={group.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`gvis-row-${group.key}`}>
                    <div>
                      <p className="font-medium text-slate-900">{group.label}</p>
                      <p className="text-xs text-slate-500">{group.desc}</p>
                    </div>
                    <Switch
                      data-testid={`gvis-toggle-${group.key}`}
                      checked={!!globalVisibility[group.key]}
                      onCheckedChange={(checked) => setGlobalVisibility({ ...globalVisibility, [group.key]: checked })}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800 border-b pb-2 mb-2">Execution Modules</p>
              <div className="space-y-2">
                {NON_STRATEGY_ITEMS.map((section) => (
                  <div key={section.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`gvis-row-${section.key}`}>
                    <div>
                      <p className="font-medium text-slate-900">{section.label}</p>
                      <p className="text-xs text-slate-500">{section.desc}</p>
                    </div>
                    <Switch
                      data-testid={`gvis-toggle-${section.key}`}
                      checked={!!globalVisibility[section.key]}
                      onCheckedChange={(checked) => setGlobalVisibility({ ...globalVisibility, [section.key]: checked })}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGlobalVisibilityDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveGlobalVisibility} disabled={savingVisibility}>
              {savingVisibility && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Defaults
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* User Profile View Dialog */}
      <Dialog open={profileDialog.open} onOpenChange={(open) => setProfileDialog({ open, user: profileDialog.user })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-500" />
              Company Profile: {profileDialog.user?.name}
            </DialogTitle>
            <DialogDescription>
              {profileDialog.user?.email}
            </DialogDescription>
          </DialogHeader>
          
          {loadingProfile ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            </div>
          ) : profileData ? (
            <div className="space-y-6 py-4">
              {/* Activity Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-teal-600">{profileData.pipeline_count}</p>
                  <p className="text-xs text-slate-500">Pipeline Items</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-teal-600">{profileData.investor_network_count}</p>
                  <p className="text-xs text-slate-500">Investor Network</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-teal-600">{profileData.strategy_completed ? "Yes" : "No"}</p>
                  <p className="text-xs text-slate-500">Strategy Done</p>
                </div>
              </div>

              {profileData.profile ? (
                <>
                  {/* Company Info */}
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Company Information
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-slate-500">Company Name</p>
                        <p className="font-medium">{profileData.profile.company_name || "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Company Type</p>
                        <p className="font-medium">{profileData.profile.company_type || profileData.profile.archetype || "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Location</p>
                        <p className="font-medium">{profileData.profile.location || "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Funding Stage</p>
                        <p className="font-medium">{profileData.profile.funding_stage || "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Development Stage</p>
                        <p className="font-medium">{profileData.profile.development_stage || "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Runway</p>
                        <p className="font-medium">{profileData.profile.runway || "—"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Scientific Info */}
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Scientific Focus</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-slate-500">Indications</p>
                        <p className="font-medium">
                          {profileData.profile.indications?.length > 0 
                            ? profileData.profile.indications.join(", ") 
                            : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Modality</p>
                        <p className="font-medium">{profileData.profile.modality || "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Target Focus</p>
                        <p className="font-medium">{profileData.profile.target_focus || "—"}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Target Name</p>
                        <p className="font-medium">{profileData.profile.target_name || "—"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {(profileData.profile.description || profileData.profile.one_liner) && (
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-3">Description</h3>
                      {profileData.profile.one_liner && (
                        <p className="text-sm text-slate-700 font-medium mb-2">
                          {profileData.profile.one_liner}
                        </p>
                      )}
                      {profileData.profile.description && (
                        <p className="text-sm text-slate-600">
                          {profileData.profile.description}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Achievements */}
                  {(profileData.profile.scientific_achievements || profileData.profile.business_achievements) && (
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-3">Achievements</h3>
                      {profileData.profile.scientific_achievements && (
                        <div className="mb-3">
                          <p className="text-slate-500 text-sm mb-1">Scientific Achievements</p>
                          <p className="text-sm text-slate-700">{profileData.profile.scientific_achievements}</p>
                        </div>
                      )}
                      {profileData.profile.business_achievements && (
                        <div>
                          <p className="text-slate-500 text-sm mb-1">Business Achievements</p>
                          <p className="text-sm text-slate-700">{profileData.profile.business_achievements}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Valuation */}
                  {(profileData.profile.valuation_amount || profileData.profile.valuation_year) && (
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-3">Valuation</h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-slate-500">Amount</p>
                          <p className="font-medium">{profileData.profile.valuation_amount || "—"}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Year</p>
                          <p className="font-medium">{profileData.profile.valuation_year || "—"}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-slate-400 text-right">
                    Last updated: {profileData.profile.updated_at 
                      ? new Date(profileData.profile.updated_at).toLocaleString() 
                      : "—"}
                  </p>
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>This user hasn't filled out their company profile yet.</p>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialog({ open: false, user: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create User Dialog */}
      <Dialog open={createUserDialog} onOpenChange={setCreateUserDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-teal-500" />
              Create New User
            </DialogTitle>
            <DialogDescription>
              Create a new user account with custom module visibility settings.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* User Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-user-name">Name *</Label>
                <Input
                  id="new-user-name"
                  placeholder="John Doe"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-user-company">Company</Label>
                <Input
                  id="new-user-company"
                  placeholder="Acme Inc."
                  value={newUser.company}
                  onChange={(e) => setNewUser({...newUser, company: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-user-email">Email *</Label>
              <Input
                id="new-user-email"
                type="email"
                placeholder="john@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-user-password">Password * (min 8 characters)</Label>
              <Input
                id="new-user-password"
                type="password"
                placeholder="••••••••"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
              />
            </div>
            
            {/* Module Visibility */}
            <div className="border-t pt-4 mt-4">
              <Label className="text-base font-semibold mb-3 block">Module Visibility</Label>
              <p className="text-sm text-slate-500 mb-3">
                Select which modules this user can access. Locked modules will show with a lock icon.
              </p>
              <div className="space-y-4 max-h-[50vh] overflow-y-auto">
                <div>
                  <p className="text-xs font-semibold text-slate-700 border-b pb-1 mb-1">Strategy Modules</p>
                  {STRATEGY_GROUPS.map((group) => (
                    <div key={group.key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{group.label}</p>
                        <p className="text-xs text-slate-500">{group.desc}</p>
                      </div>
                      <Switch
                        checked={!!newUser.visibility[group.key]}
                        onCheckedChange={(checked) => setNewUser({
                          ...newUser,
                          visibility: { ...newUser.visibility, [group.key]: checked }
                        })}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-700 border-b pb-1 mb-1">Execution Modules</p>
                  {NON_STRATEGY_ITEMS.map((section) => (
                    <div key={section.key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{section.label}</p>
                        <p className="text-xs text-slate-500">{section.desc}</p>
                      </div>
                      <Switch
                        checked={!!newUser.visibility[section.key]}
                        onCheckedChange={(checked) => setNewUser({
                          ...newUser,
                          visibility: { ...newUser.visibility, [section.key]: checked }
                        })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser} disabled={creatingUser} className="bg-teal-600 hover:bg-teal-700">
              {creatingUser && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============== Module Completion – League Table Dialog ============== */}
      <Dialog open={leagueDialog} onOpenChange={setLeagueDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="completion-league-dialog">
          <DialogHeader>
            <DialogTitle>Module Completion — All Companies</DialogTitle>
            <DialogDescription>Ranked by overall completion across the 12 modules. Click a row for the per-module breakdown.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between border-b pb-2">
            <p className="text-sm text-slate-500">{leagueLoading ? "Loading…" : `${leagueRows.length} companies`}</p>
            <Button variant="outline" size="sm" onClick={downloadCompletionCsv} data-testid="download-completion-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead className="text-right">Overall</TableHead>
                  <TableHead className="text-right">Modules started</TableHead>
                  <TableHead className="text-right">Modules complete</TableHead>
                  <TableHead>Last active</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leagueRows.map((r) => (
                  <TableRow key={r.user_id} data-testid={`league-row-${r.user_id}`}>
                    <TableCell>
                      <p className="font-medium text-slate-800">{r.company_name}</p>
                      <p className="text-xs text-slate-500">{r.email}</p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${r.overall_percent >= 80 ? "bg-emerald-500" : r.overall_percent >= 30 ? "bg-amber-500" : "bg-slate-300"}`} style={{ width: `${r.overall_percent}%` }} />
                        </div>
                        <span className="text-sm font-semibold text-slate-800 w-10 text-right">{r.overall_percent}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-sm text-slate-700">{r.modules_started}/12</TableCell>
                    <TableCell className="text-right text-sm text-slate-700">{r.modules_complete}/12</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.last_active ? new Date(r.last_active).toLocaleDateString() : "—"}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => { setLeagueDialog(false); openUserCompletion({ user_id: r.user_id, email: r.email, company_name: r.company_name }); }} data-testid={`league-view-${r.user_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!leagueLoading && leagueRows.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No companies yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============== Module Completion – Per-User Detail Dialog ============== */}
      <Dialog open={completionDialog.open} onOpenChange={(open) => !open && setCompletionDialog({ open: false, user: null, data: null })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-completion-dialog">
          <DialogHeader>
            <DialogTitle>{completionDialog.user?.company_name || completionDialog.user?.email || "Company"} — Module Completion</DialogTitle>
            <DialogDescription>
              {completionDialog.data ? (
                <>Overall <strong>{completionDialog.data.overall_percent}%</strong> · {completionDialog.data.modules_started}/12 started · {completionDialog.data.modules_complete}/12 complete{completionDialog.data.last_active ? ` · last active ${new Date(completionDialog.data.last_active).toLocaleDateString()}` : ""}</>
              ) : "Loading…"}
            </DialogDescription>
          </DialogHeader>
          {completionLoading && <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
          {completionDialog.data && (
            <div className="space-y-4 py-2">
              {["market", "clinical", "technology", "milestones"].map((group) => {
                const groupLabel = { market: "Market Strategy", clinical: "Clinical & Regulatory", technology: "Technology & Business Model", milestones: "Milestones, Team & Funding" }[group];
                const items = (completionDialog.data.modules || []).filter((m) => m.group === group);
                return (
                  <div key={group}>
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide border-b pb-1 mb-2">{groupLabel}</p>
                    <div className="space-y-2">
                      {items.map((m) => (
                        <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50" data-testid={`completion-row-${m.id}`}>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-800">{m.label}</p>
                            <p className="text-[11px] text-slate-500">
                              {m.status === "not_started" && "Not started"}
                              {m.status === "in_progress" && `In progress — ${m.filled}/${m.total} fields filled`}
                              {m.status === "complete" && `Complete (${m.filled}/${m.total})`}
                            </p>
                          </div>
                          <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full ${m.status === "complete" ? "bg-emerald-500" : m.status === "in_progress" ? "bg-amber-500" : "bg-slate-300"}`} style={{ width: `${m.percent}%` }} />
                          </div>
                          <span className="text-sm font-semibold text-slate-700 w-10 text-right">{m.percent}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompletionDialog({ open: false, user: null, data: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
